import { MonthReview } from "@/components/ai/MonthReview";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Flame, PlayCircle, UserPlus, Gift, Bell, CalendarCheck, Loader2, Shield, ArrowRight, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PointsPausedNotice } from "@/components/PointsPausedNotice";
import { PageExplain } from "@/components/PageExplain";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white rounded-2xl p-4 border border-pink-100" data-testid={testId}>
    <Icon className="w-5 h-5 text-rose-500" />
    <p className="mt-2 text-2xl font-extrabold font-display">{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);

const Quick = ({ to, icon: Icon, title, text, testId }) => (
  <Link to={to} data-testid={testId} className="group bg-white rounded-2xl p-5 border border-pink-100 hover:border-rose-300 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
    <Icon className="w-6 h-6 text-rose-500 group-hover:scale-110 transition-transform" />
    <p className="mt-3 font-bold">{title}</p>
    <p className="text-xs text-slate-500 mt-1">{text}</p>
  </Link>
);

const streakProgress = (streak, length, claimedToday) => {
  if (streak === 0) return 0;
  const mod = streak % length;
  if (mod !== 0) return mod;
  return claimedToday ? length : 0;
};

const dayClass = (active, last) => {
  if (active) return "pink-gradient text-white shadow-md shadow-rose-200";
  if (last) return "bg-amber-50 border-2 border-dashed border-amber-300 text-amber-600";
  return "bg-rose-50 text-rose-300";
};

const StreakTracker = ({ streak, length, bonus, claimedToday }) => {
  const done = streakProgress(streak, length, claimedToday);
  const left = length - done;
  return (
    <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100" data-testid="streak-tracker">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-bold flex items-center gap-2"><Flame className="w-5 h-5 text-orange-500" /> {length}-Tage-Serie</h3>
        <span className="text-xs font-bold px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="streak-bonus-info">
          {done === length ? `Bonus +${bonus} kassiert! 🎉` : `Noch ${left} ${left === 1 ? "Tag" : "Tage"} bis +${bonus}`}
        </span>
      </div>
      <div className="grid grid-cols-7 gap-1.5 sm:gap-3">
        {Array.from({ length }, (_, i) => {
          const active = i < done;
          const last = i === length - 1;
          return (
            <div key={`day-${i + 1}`} data-testid={`streak-day-${i + 1}`} className={`aspect-square rounded-2xl flex flex-col items-center justify-center text-[10px] sm:text-xs font-bold transition-colors duration-300 ${dayClass(active, last)}`}>
              {last ? <Gift className="w-4 h-4 sm:w-5 sm:h-5" /> : <Flame className="w-4 h-4 sm:w-5 sm:h-5" />}
              <span className="mt-0.5">Tag {i + 1}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const TxList = ({ items }) => (
  <ul className="divide-y divide-pink-50" data-testid="transactions-list">
    {items.length === 0 && <li className="py-6 text-sm text-slate-400 text-center">Noch keine Punkte – leg los!</li>}
    {items.map((t) => (
      <li key={t.id} className="py-3 flex items-center justify-between gap-3">
        <div className="min-w-0"><p className="text-sm font-medium truncate">{t.reason}</p><p className="text-xs text-slate-400">{fmtDate(t.created_at)}</p></div>
        <span className={`shrink-0 text-sm font-bold ${t.amount >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</span>
      </li>
    ))}
  </ul>
);

const dashboardSteps = (st, sum) => [
  `Hol dir hier jeden Tag deinen Login-Bonus (+${st.points_daily} Punkte). Nach ${sum.streak_length} Tagen am Stück gibt's +${st.points_streak_bonus} Extra-Punkte – wer einen Tag auslässt, startet wieder bei Tag 1.`,
  "Mach bei Spielen, Tagesquiz, Community-Aufgaben und im Feed mit – jede Aktivität bringt dir Punkte.",
  `Lade Freunde ein (+${st.points_referral} pro Anmeldung) und tausche Punkte unter „Geschenke“ ein.`,
];

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [sum, setSum] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const load = useCallback(() => api.get("/points/summary").then(({ data }) => setSum(data)), []);
  useEffect(() => { load(); }, [load]);

  const claimDaily = async () => {
    setClaiming(true);
    try {
      const { data } = await api.post("/points/daily");
      toast.success(`+${data.awarded} Punkte! Tag ${data.streak} deiner Serie 🔥`);
      if (data.bonus) toast.success(`Serien-Bonus! ${data.streak} Tage am Stück: +${data.bonus} Extra-Punkte 🎉`, { duration: 6000 });
      if (data.winback) toast.success(`Schön, dass du wieder da bist! +${data.winback} Willkommen-zurück-Bonus 💖`, { duration: 6000 });
      await Promise.all([load(), refreshUser()]);
    } catch (e) { toast.error(formatErr(e)); } finally { setClaiming(false); }
  };

  if (!sum) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  const st = sum.settings;

  return (
    <div className="space-y-6">
      <PointsPausedNotice />
      {user?.role === "admin" && (
        <Link to="/admin" data-testid="dashboard-admin-card" className="flex items-center gap-4 p-4 sm:p-5 rounded-3xl bg-slate-900 text-white hover:bg-slate-800 transition-colors">
          <div className="w-11 h-11 rounded-2xl pink-gradient flex items-center justify-center shrink-0"><Shield className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0"><p className="font-bold">Admin-Bereich öffnen</p><p className="text-xs text-slate-300">Beiträge, Geschenke & Einlösungen verwalten, Push senden</p></div>
          <ArrowRight className="w-5 h-5 shrink-0" />
        </Link>
      )}
      <PageExplain testId="dashboard-explain" steps={dashboardSteps(st, sum)} />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 relative overflow-hidden rounded-3xl pink-gradient text-white p-6 sm:p-8 pink-glow fade-up">
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-white/10" />
          <div className="absolute right-10 -bottom-16 w-40 h-40 rounded-full bg-white/10" />
          <p className="text-sm text-pink-100">Hallo {user?.name?.split(" ")[0]} 👋</p>
          <p className="mt-1 text-xs uppercase tracking-widest text-pink-100">Dein Punktestand</p>
          <p className="text-5xl sm:text-6xl font-extrabold font-display mt-1 flex items-center gap-3" data-testid="dashboard-points">
            <Coins className="w-10 h-10" /> {sum.points}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={claimDaily} disabled={sum.daily_claimed_today || claiming} data-testid="claim-daily-btn"
              className={`${sum.daily_claimed_today ? "" : "shine-btn"} relative overflow-hidden inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white text-rose-600 font-bold hover:scale-[1.03] active:scale-95 transition-transform duration-200 disabled:opacity-70 disabled:hover:scale-100`}>
              {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck className="w-5 h-5" />}
              {sum.daily_claimed_today ? "Heute abgeholt ✓" : `Login-Bonus abholen (+${st.points_daily})`}
            </button>
            <Link to="/geschenke" data-testid="dashboard-shop-btn" className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white/15 border border-white/30 font-semibold hover:bg-white/25 transition-colors">
              <Gift className="w-5 h-5" /> Einlösen
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-1 gap-3">
          <Stat icon={Flame} label="Tage-Serie" value={sum.streak} testId="stat-streak" />
          <Stat icon={PlayCircle} label="Spiele" value={sum.games_played ?? 0} testId="stat-games" />
          <Stat icon={UserPlus} label="Freunde" value={sum.referrals} testId="stat-referrals" />
        </div>
      </div>

      <StreakTracker streak={sum.streak} length={sum.streak_length} bonus={st.points_streak_bonus} claimedToday={sum.daily_claimed_today} />

      <MonthReview />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Quick to="/spiele" icon={PlayCircle} title="Spiele" text="Spielen & Punkte holen" testId="quick-games" />
        <Quick to="/einladen" icon={UserPlus} title="Einladen" text={`+${st.points_referral} pro Freund`} testId="quick-invite" />
        <Quick to="/geschenke" icon={Gift} title="Geschenke" text="Punkte eintauschen" testId="quick-rewards" />
        <Quick to="/community" icon={Users} title="Mitglieder" text="Suchen & Profile ansehen" testId="quick-community" />
        <Quick to="/punkte-sammeln" icon={Bell} title="So sammelst du" text="Alle Punkte-Wege" testId="quick-guide" />
      </div>

      <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100">
        <h3 className="text-lg font-bold mb-2">Punkte-Verlauf</h3>
        <TxList items={sum.transactions.slice(0, 15)} />
      </div>
    </div>
  );
}
